import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { streamResearch, type ResearchSourceSnapshot, type ResearchStreamEvent } from './research-api'
import { vaultKeys } from '@/features/vault/use-vault'
import {
  abortRun,
  beginRun,
  clearRun,
  isRunController,
  setRunAbortController,
  updateRunState,
  useRunStore,
} from '@/features/runs/run-store'

export type ResearchPhase =
  | 'queued'
  | 'web'
  | 'academic'
  | 'grok'
  | 'youtube'
  | 'podcasts'
  | 'legal'
  | 'sources_loaded'
  | 'synthesize'
  | 'write_sources'
  | 'write_dossier'
  | 'done'
  | 'error'

export interface ResearchProgress {
  topic: string
  phase: ResearchPhase
  detail: string
  progressByPhase: Partial<Record<ResearchPhase, { done: number; total: number; detail?: string }>>
  sources: ResearchSourceSnapshot[]
  result?: {
    dossier_id: string
    dossier_path: string
    key_findings: string[]
    tier_counts: { tier_1: number; tier_2: number; tier_3: number }
  }
  error?: string
}

interface UseResearchReturn {
  start: (topic: string, sessionId?: string | null, discovery?: 'tavily' | 'gemini' | 'grok' | 'parallel') => void
  cancel: () => void
  reset: () => void
  state: ResearchProgress | null
  /** Session, an die der laufende/letzte Run gebunden ist (M2-B Run-Survival). */
  sessionId: string | null
  isRunning: boolean
}

export function useResearch(): UseResearchReturn {
  const qc = useQueryClient()
  // Globaler Store statt lokalem State: der Run (SSE-Fetch + Fortschritt)
  // ueberlebt das Unmounten der Chat-Page (M2-B Run-Survival).
  const slice = useRunStore((s) => s.research)

  const start = useCallback(
    (topic: string, sessionId?: string | null, discovery?: 'tavily' | 'gemini' | 'grok' | 'parallel') => {
      // Bestehenden Lauf abbrechen
      abortRun('research')
      const controller = new AbortController()
      setRunAbortController('research', controller)

      beginRun(
        'research',
        {
          topic,
          phase: 'queued',
          detail: 'Research wird gestartet…',
          progressByPhase: {},
          sources: [],
        },
        sessionId ?? null,
      )

      void streamResearch({
        topic,
        discovery,
        sessionId: sessionId ?? undefined,
        signal: controller.signal,
        onEvent: (event: ResearchStreamEvent) => {
          // Events eines abgeloesten/abgebrochenen Laufs ignorieren.
          if (!isRunController('research', controller)) return
          updateRunState('research', (prev) => {
            if (!prev) return prev
            switch (event.type) {
              case 'status': {
                const phase = (event.phase as ResearchPhase) ?? prev.phase
                const next: ResearchProgress = {
                  ...prev,
                  phase,
                  detail: event.detail ?? prev.detail,
                }
                if (event.progress) {
                  next.progressByPhase = {
                    ...prev.progressByPhase,
                    [phase]: { ...event.progress, detail: event.detail },
                  }
                }
                if (event.phase === 'sources_loaded' && event.sources) {
                  next.sources = event.sources
                }
                return next
              }
              case 'source':
                if (prev.sources.some((source) => source.url === event.source.url)) return prev
                return { ...prev, sources: [...prev.sources, event.source] }
              case 'done':
                return {
                  ...prev,
                  phase: 'done',
                  detail: 'Dossier fertig',
                  result: {
                    dossier_id: event.dossier_id,
                    dossier_path: event.dossier_path,
                    key_findings: event.key_findings,
                    tier_counts: event.tier_counts,
                  },
                }
              case 'error':
                return { ...prev, phase: 'error', error: event.message }
            }
          })

          if (event.type === 'done') {
            void qc.invalidateQueries({ queryKey: vaultKeys.files() })
            toast.success(`Dossier erstellt: ${event.dossier_path}`)
          }
          if (event.type === 'error') {
            toast.error(event.message || 'Research fehlgeschlagen')
          }
        },
      }).catch((err) => {
        if (controller.signal.aborted) return
        const message = err instanceof Error ? err.message : 'Research fehlgeschlagen'
        toast.error(message)
        updateRunState('research', (prev) => (prev ? { ...prev, phase: 'error', error: message } : prev))
      })
    },
    [qc],
  )

  const cancel = useCallback(() => {
    abortRun('research')
  }, [])

  const reset = useCallback(() => {
    abortRun('research')
    clearRun('research')
  }, [])

  const state = slice.state
  const isRunning = state?.phase != null && state.phase !== 'done' && state.phase !== 'error'

  return { start, cancel, reset, state, sessionId: slice.sessionId, isRunning: !!isRunning }
}
